import { useRef, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, User, Briefcase, Code2, BookOpen, Mail } from 'lucide-react';
import { CircleMenu } from './circle-menu';
import { resolveUrl } from '../../lib/api';
import { useSiteSettings, useTaglines } from '../../lib/content';
import { trackPageView } from '../../lib/analytics';

const navItems = [
  { label: 'Home',       id: 'home',    icon: <Home size={16} /> },
  { label: 'About Me',   id: 'about',   icon: <User size={16} /> },
  { label: 'Experience', id: 'experience', icon: <Briefcase size={16} /> },
  { label: 'Projects',   id: 'works',   icon: <Code2 size={16} /> },
  { label: 'Blogs',      id: 'blogs',   icon: <BookOpen size={16} /> },
  { label: 'Contact Me', id: 'contact', icon: <Mail size={16} /> },
];

// Fallback invitations, used until the Worker's AI-written ones arrive (or if
// generation fails). Deliberately vague about volume — the live copy is
// generated from the actual content so it can't claim projects that aren't
// there. Keyed by data-page-id so panels outside navItems are covered too.
const FALLBACK_PROMPTS: Record<string, string> = {
  home: "Hey! I'm Varun's AI — ask me anything about his work.",
  about: "That's Varun's background. Want the short version, or the detail?",
  experience: 'Ask me what he actually did in any of these roles.',
  works: 'Ask me what he has been building lately.',
  blogs: 'Ask me what he has been writing about.',
  contact: 'I can pass a message straight to Varun — just tell me who you are.',
};

interface SiteLayoutProps {
  children: React.ReactNode;
  onChatOpen?: () => void;
}

export default function SiteLayout({ children, onChatOpen }: SiteLayoutProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [activeSection, setActiveSection] = useState('home');
  const { pathname } = useLocation();
  // Assistant nudge: a wiggle plus a one-line invitation, shown a few seconds
  // in. Dismissed for the session once the visitor engages, so it invites
  // rather than nags.
  const [showBubble, setShowBubble] = useState(false);
  const [nudge, setNudge] = useState(false);
  const [nudgeDone, setNudgeDone] = useState(() => {
    try {
      return sessionStorage.getItem('chat_nudge_seen') === '1';
    } catch {
      return false; // private mode / storage blocked
    }
  });

  const navigate = useNavigate();
  const [clock, setClock] = useState('');
  const { settings: site } = useSiteSettings();
  const taglines = useTaglines();
  const isNavigatingRef = useRef(false);

  // Live clock
  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      setClock(now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Asia/Kolkata' }));
    };
    updateClock();
    const interval = setInterval(updateClock, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const pages = container.querySelectorAll('[data-page-id]');
      pages.forEach((page, idx) => {
        const rect = page.getBoundingClientRect();
        if (rect.top <= window.innerHeight / 2 && rect.bottom >= window.innerHeight / 2) {
          setActiveIdx(idx);
          // Sections live on one route, so record each as its own virtual page
          // view — otherwise every visit would only ever log "/".
          const id = page.getAttribute('data-page-id');
          if (id) {
            setActiveSection(id);
            trackPageView(id === 'home' ? '/' : `/${id}`);
          }
        }
      });
      isNavigatingRef.current = false;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isNavigatingRef.current) return;

      const pages = container.querySelectorAll('[data-page-id]');
      let nextIdx = activeIdx;

      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        nextIdx = Math.min(activeIdx + 1, pages.length - 1);
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        nextIdx = Math.max(activeIdx - 1, 0);
      } else {
        return;
      }

      if (nextIdx !== activeIdx) {
        isNavigatingRef.current = true;
        const page = pages[nextIdx] as HTMLElement;
        if (page) page.scrollIntoView({ behavior: 'smooth' });
      }
    };

    // Run once on mount so a visitor who never scrolls is still recorded.
    handleScroll();
    container.addEventListener('scroll', handleScroll);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeIdx]);

  // Sections are panels on "/", but /about, /works etc. are real routes so they
  // can be linked, shared and indexed. On load, jump to the panel named by the
  // URL instead of always starting at the top.
  useEffect(() => {
    const id = pathname.replace(/^\//, '') || 'home';
    const container = containerRef.current;
    if (!container) return;
    const target = container.querySelector(`[data-page-id="${id}"]`) as HTMLElement | null;
    if (!target || id === 'home') return;
    // Wait a frame so layout has settled before measuring.
    const t = requestAnimationFrame(() => target.scrollIntoView({ behavior: 'auto' }));
    return () => cancelAnimationFrame(t);
  }, [pathname]);

  // Offer a line about whatever section the visitor is reading. Re-runs on
  // each section change so the message stays relevant, but goes quiet for good
  // once they engage or dismiss it.
  useEffect(() => {
    if (nudgeDone) return;
    // Longer pause on first load — let them look before interrupting.
    const delay = activeSection === 'home' ? 6000 : 2500;
    const wiggleAt = setTimeout(() => setNudge(true), delay);
    const bubbleAt = setTimeout(() => setShowBubble(true), delay + 400);
    // Retract on its own; the wiggle keeps cycling quietly.
    const hideAt = setTimeout(() => setShowBubble(false), delay + 9000);
    return () => {
      clearTimeout(wiggleAt);
      clearTimeout(bubbleAt);
      clearTimeout(hideAt);
    };
  }, [nudgeDone, activeSection]);

  const dismissNudge = () => {
    setShowBubble(false);
    setNudge(false);
    setNudgeDone(true);
    try {
      sessionStorage.setItem('chat_nudge_seen', '1');
    } catch { /* storage blocked — it just re-nudges next load */ }
  };

  const openChat = () => {
    dismissNudge();
    onChatOpen?.();
  };

  const scrollToPage = (idx: number) => {
    if (!containerRef.current) return;
    const pages = containerRef.current.querySelectorAll('[data-page-id]');
    const page = pages[idx] as HTMLElement;
    if (!page) return;
    page.scrollIntoView({ behavior: 'smooth' });
    // Keep the URL in step so the section stays shareable and the back button
    // works. replace: the deep-link effect would otherwise re-scroll us.
    const id = page.getAttribute('data-page-id');
    if (id) navigate(id === 'home' ? '/' : `/${id}`, { replace: true });
  };

  return (
    <div className="scroll-snap-container" ref={containerRef}>
      {/* Global topbar — brand, location, clock, status */}
      <div className="topbar" role="banner">
        <div className="brand">
          <span className="dot" />
          <span style={{ fontWeight: 700, color: 'var(--color-text)' }}>CH&nbsp;VARUN</span>
        </div>
        <div className="meta">
          <span>{site?.location?.toUpperCase() || 'HYDERABAD, IN'}</span>
          <span className="sep" />
          <span className="clock">{clock}</span>
          <span className="sep" />
          {site?.resume_url ? (
            <a
              href={resolveUrl(site.resume_url) ?? '#'}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--color-accent)', textDecoration: 'none', fontWeight: 600 }}
            >
              ↓ RESUME
            </a>
          ) : (site?.open_to_work ?? true) ? (
            <span style={{ color: '#22c55e' }}>● AVAILABLE</span>
          ) : null}
        </div>
      </div>

      {/* Keyboard hint */}
      <div className="cmd-hint">
        <kbd>↑</kbd><kbd>↓</kbd><kbd>←</kbd><kbd>→</kbd>&nbsp;Pages
      </div>

      {children}

      {/* Circle Menu — animated navigation index. Active item auto-syncs with current page on scroll. */}
      <div
        className="circle-menu-wrapper"
        style={{
          position: 'fixed',
          right: '3rem',
          top: '22%',
          transform: 'translateY(-50%)',
          zIndex: 50,
          pointerEvents: 'auto',
        }}
      >
        <CircleMenu
          items={navItems.map((n) => ({ label: n.label, icon: n.icon, href: '#' + n.id }))}
          activeIndex={activeIdx}
          onItemClick={(idx) => scrollToPage(idx)}
        />
      </div>

      {/* Nudge bubble — invites the visitor to try the assistant. */}
      {showBubble && !nudgeDone && (
        <div key={activeSection} className="chat-bubble" role="status" onClick={openChat}>
          {taglines?.[activeSection] ?? FALLBACK_PROMPTS[activeSection] ?? FALLBACK_PROMPTS.home}
          <button
            className="chat-bubble-dismiss"
            aria-label="Dismiss"
            onClick={(e) => { e.stopPropagation(); dismissNudge(); }}
          >
            ×
          </button>
        </div>
      )}

      {/* Floating chat button — always visible, opens popup */}
      <button
        className={`chat-fab${nudge && !nudgeDone ? ' nudge' : ''}`}
        aria-label="Chat with Varun's assistant"
        onClick={openChat}
      >
        <div className="pulse-ring" />
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12h-8v-2h8v2zm0-4h-8V8h8v2z" />
        </svg>
      </button>
    </div>
  );
}
